import {
  failure,
  isFailure,
  isOk,
  isPending,
  ok,
  pending,
  Portfolio,
  Result,
} from "@/common/common";
import {
  BooleanFieldModel,
  LocalDateFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
  TimeFieldModel,
} from "@/common/commonForms";
import {
  CompanyIdentifier,
  CompanyLookupResponse,
  companyRegistryApiClient,
  CompanySearchHit,
  CountryOption,
  EntityType,
  formatIdentifier,
  identifierFor,
  isAcceptedIdentifier,
  isValidIdentifier,
  normalizeIdentifier,
  primaryIdentifier,
  isValidSubdivision,
  preselectEntityType,
  RegistryCountry,
  requiresSubdivision,
  Subdivision,
  subdivisionOptions,
} from "@/companyRegistry/companyRegistryApiClient";
import {
  enterpriseAccountApiClient,
  NewCompanyRejection,
  ParentOwnershipRequest,
  ParentOwnershipTerms,
} from "@/enterpriseAccounts/enterpriseAccountApiClient";
import {
  AccountSubscription,
  OWNERSHIP_REGISTER_PRODUCT,
} from "@/enterpriseAccounts/enterpriseAccountsDomain";
import { DCEvent, debounceEvents, HttpResponse, UpdateStoreEvent } from "@/events/eventsDomain";
import { Language } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { combineDateAndTime, DEFAULT_TIME } from "@/utils/Dates";
import { orderlessFilter } from "@/utils/filter";
import { newUuid } from "@/utils/uuid";
import { shareTypeLabel, ShareTypeCode } from "@/common/share";

export type AdminOption = { id: string; name: string; isAccountAdmin: boolean };

export type ParentOption = { orgNo: string; name: string };

export type ParentOwnershipBasis = "percent-held" | ShareTypeCode;

export type ParentHolding = {
  id: string;
  basisModel: RadioFieldModel<ParentOwnershipBasis>;
  heldModel: NumberFieldModel;
  totalModel: NumberFieldModel;
};

const shareClassCodes = Object.values(ShareTypeCode).filter(
  (code) => code !== ShareTypeCode.UNKNOWN,
);

const FULL_OWNERSHIP = 100;
const OWNERSHIP_DECIMALS = 4;

export type AddCompanyState = {
  accountId: string;
  accountName: string;
  lang: Language;
  countriesResult?: Result<CountryOption[]>;
  country?: CountryOption;
  rulesResult?: Result<RegistryCountry>;
  entityTypeModel: RadioFieldModel<EntityType | null>;
  identifierModel: StringFieldModel;
  subdivisionOptionModel: RadioFieldModel<Subdivision | null>;
  nameModel: StringFieldModel;
  tinNumberModel: StringFieldModel;
  acceptTerms: BooleanFieldModel;
  identifierChecked: boolean;
  lookupResult?: Result<CompanyLookupResponse>;
  nameSearchResult?: Result<CompanySearchHit[]>;
  subscriptionModel: RadioFieldModel<AccountSubscription | null>;
  registerModel: RadioFieldModel<AccountSubscription | null>;
  portfolioModel: RadioFieldModel<Portfolio | null>;
  portfoliosResult?: Result<Portfolio[]>;
  adminsModel?: OptionFieldModel<AdminOption>;
  parentModel: RadioFieldModel<ParentOption | null>;
  parentHoldings: ParentHolding[];
  parentOwnershipModel: NumberFieldModel;
  parentRegistrationDateModel: LocalDateFieldModel;
  parentRegistrationTimeModel: TimeFieldModel;
  submitResult?: Result<TMessage, TMessage>;
};

const subdivisionOptionModelFor = (country: RegistryCountry | undefined, lang: Language) => {
  const mandatory = requiresSubdivision(country);
  return new RadioFieldModel<Subdivision | null>({
    value: null,
    required: mandatory,
    options: [null, ...subdivisionOptions(country, lang)],
    idFn: (option) => option?.code ?? "",
    labelFn: (option) =>
      option
        ? { translated: option.label[lang] }
        : { key: mandatory ? "add-company.choose-jurisdiction" : "add-company.not-stated" },
  });
};

const typedByRegister = (country: RegistryCountry | undefined): boolean =>
  country?.verification.entityTypeFromRegister === true;

const entityTypeModelFor = (country: RegistryCountry | undefined, lang: Language) =>
  new RadioFieldModel<EntityType | null>({
    value: typedByRegister(country) ? null : preselectEntityType(country),
    options: [null, ...[...(country?.entityTypes ?? [])].sort((a, b) => a.rank - b.rank)],
    idFn: (option) => option?.code ?? "",
    labelFn: (option) =>
      option ? { translated: option.label[lang] } : { key: "add-company.choose-entity-type" },
  });

const defaultAddCompanyState = (
  accountId: string,
  accountName: string,
  lang: Language,
  admins: AdminOption[],
  subscriptions: AccountSubscription[],
  registers: AccountSubscription[],
  parents: ParentOption[],
): AddCompanyState => ({
  accountId,
  accountName,
  lang,
  entityTypeModel: entityTypeModelFor(undefined, lang),
  identifierChecked: false,
  identifierModel: new StringFieldModel({ required: true }),
  subdivisionOptionModel: subdivisionOptionModelFor(undefined, lang),
  nameModel: new StringFieldModel({ required: true }),
  tinNumberModel: new StringFieldModel(),
  acceptTerms: new BooleanFieldModel({ requiredTrue: true, value: true }),
  subscriptionModel: new RadioFieldModel<AccountSubscription | null>({
    value:
      subscriptions.find((option) => option.productName === OWNERSHIP_REGISTER_PRODUCT) ?? null,
    options: [null, ...subscriptions],
    idFn: (option) => option?.productName ?? "none",
    labelFn: (option) =>
      option ? { translated: option.label } : { key: "enterprise-account.no-subscription" },
  }),
  registerModel: new RadioFieldModel<AccountSubscription | null>({
    value: null,
    options: registers,
    idFn: (option) => option?.productName ?? "none",
    labelFn: (option) =>
      option ? { translated: option.label } : { key: "enterprise-account.no-subscription" },
  }),
  portfolioModel: new RadioFieldModel<Portfolio | null>({
    value: null,
    options: [null],
    idFn: (option) => option?.id ?? "none",
    labelFn: (option) => (option ? { translated: option.name } : { key: "optional" }),
  }),
  adminsModel:
    admins.length > 0
      ? new OptionFieldModel<AdminOption>({
          options: admins,
          selectedIds: admins.map((admin) => admin.id),
          idFn: (admin) => admin.id,
          labelFn: (admin) => ({ translated: admin.name }),
        })
      : undefined,
  parentModel: new RadioFieldModel<ParentOption | null>({
    value: null,
    options: parents,
    idFn: (option) => option?.orgNo ?? "none",
    labelFn: (option) => (option ? { translated: option.name } : { key: "add-company.no-parent" }),
  }),
  parentHoldings: [newParentHolding("percent-held", ["percent-held", ...shareClassCodes], lang)],
  parentOwnershipModel: new NumberFieldModel({
    value: FULL_OWNERSHIP,
    required: true,
    isPositive: true,
    minDecimals: OWNERSHIP_DECIMALS,
    maxDecimals: OWNERSHIP_DECIMALS,
    locale: lang,
    validator: (value) =>
      value !== undefined && value > FULL_OWNERSHIP
        ? { key: "add-company.parent-ownership-max" }
        : undefined,
  }),
  parentRegistrationDateModel: new LocalDateFieldModel({ required: true }),
  parentRegistrationTimeModel: new TimeFieldModel({ value: DEFAULT_TIME }),
});

function newParentHolding(
  basis: ParentOwnershipBasis,
  options: ParentOwnershipBasis[],
  lang: Language,
): ParentHolding {
  return {
    id: newUuid(),
    basisModel: new RadioFieldModel<ParentOwnershipBasis>({
      value: basis,
      options,
      idFn: (option) => option,
      labelFn: (option) =>
        option === "percent-held" ? { key: "add-company.percent-held" } : shareTypeLabel(option),
    }),
    heldModel: new NumberFieldModel({
      required: true,
      isInteger: true,
      isPositiveOrZero: true,
      locale: lang,
    }),
    totalModel: new NumberFieldModel({
      required: true,
      isInteger: true,
      isPositive: true,
      locale: lang,
    }),
  };
}

const updateAddCompany = (partial: Partial<AddCompanyState>): UpdateStoreEvent => ({
  type: "update-store",
  data: { addCompany: (state) => state && { ...state, ...partial } },
});

const closeAddCompany = (): UpdateStoreEvent => ({
  type: "update-store",
  data: { addCompany: () => undefined },
});

export const openAddCompany = (
  accountId: string,
  accountName: string,
  lang: Language,
  admins: AdminOption[],
  subscriptions: AccountSubscription[],
  registers: AccountSubscription[],
  parents: ParentOption[],
): DCEvent[] => [
  {
    type: "update-store",
    data: {
      addCompany: defaultAddCompanyState(
        accountId,
        accountName,
        lang,
        admins,
        subscriptions,
        registers,
        parents,
      ),
    },
  },
  fetchCountries(),
  fetchPortfolios(accountId),
];

const fetchPortfolios = (accountId: string): DCEvent =>
  enterpriseAccountApiClient.getPortfolios(accountId, {
    onSuccess: (res: HttpResponse<Portfolio[]>) => [applyPortfolios(res.data)],
    onFailure: () => [updateAddCompany({ portfoliosResult: failure() })],
  });

const applyPortfolios = (portfolios: Portfolio[]): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    addCompany: (state) => {
      if (!state) return state;
      state.portfolioModel.updateOptions([null, ...portfolios]);
      return { ...state, portfoliosResult: ok(portfolios) };
    },
  },
});

export const cancelAddCompany = (): DCEvent[] => [closeAddCompany()];

const fetchCountries = (): DCEvent =>
  companyRegistryApiClient.getCountries({
    onSuccess: (res: HttpResponse<CountryOption[]>) => [
      updateAddCompany({ countriesResult: ok(res.data) }),
    ],
    onFailure: () => [updateAddCompany({ countriesResult: failure() })],
  });

const registerCountries = new Set(["NO", "SE"]);

export function isRegisterCountry(state: AddCompanyState): boolean {
  return state.country !== undefined && registerCountries.has(state.country.iso2);
}

function registerFor(
  registerModel: RadioFieldModel<AccountSubscription | null>,
  entityType: EntityType | null,
): AccountSubscription | null {
  return (
    registerModel.options.find(
      (option) => entityType !== null && option?.productName === entityType.subscription,
    ) ?? null
  );
}

export const selectCountry = (country: CountryOption): DCEvent[] => [
  {
    type: "update-store",
    data: {
      addCompany: (state) =>
        state && {
          ...state,
          country,
          rulesResult: pending(),
          entityTypeModel: entityTypeModelFor(undefined, state.lang),
          identifierModel: new StringFieldModel({ required: true }),
          subdivisionOptionModel: subdivisionOptionModelFor(undefined, state.lang),
          nameModel: new StringFieldModel({ required: true }),
          tinNumberModel: new StringFieldModel(),
          identifierChecked: false,
          lookupResult: undefined,
          nameSearchResult: undefined,
          submitResult: undefined,
        },
    },
  },
  companyRegistryApiClient.getCountry(country.iso2, {
    onSuccess: (res: HttpResponse<RegistryCountry>) => [applyCountryRules(res.data)],
    onFailure: () => [updateAddCompany({ rulesResult: failure() })],
  }),
];

const applyCountryRules = (country: RegistryCountry): UpdateStoreEvent =>
  updateAddCompanyWith((state) => {
    const entityTypeModel = entityTypeModelFor(country, state.lang);
    state.registerModel.setValue(registerFor(state.registerModel, entityTypeModel.value));
    return {
      ...state,
      rulesResult: ok(country),
      entityTypeModel,
      subdivisionOptionModel: subdivisionOptionModelFor(country, state.lang),
    };
  });

export const selectEntityType = (entityType: EntityType | null): UpdateStoreEvent =>
  updateAddCompanyWith((state) => {
    chooseEntityType(state, entityType);
    return { ...state };
  });

function chooseEntityType(state: AddCompanyState, entityType: EntityType | null) {
  state.entityTypeModel.setValue(entityType);
  state.registerModel.setValue(registerFor(state.registerModel, entityType));
}

export const setIdentifier = (value: string): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    addCompany: (state) => {
      if (!state) return state;
      const rule = identifierRule(state);
      state.identifierModel.setInputValue(
        rule && !isNameQuery(state, value) ? formatIdentifier(value, rule) : value,
      );
      if (!requiresSubdivision(countryRules(state))) state.subdivisionOptionModel.setValue(null);
      if (entityTypeFromRegister(state)) chooseEntityType(state, null);
      return { ...state, identifierChecked: false, lookupResult: undefined };
    },
  },
});

const nameSearchCountries = new Set(["NO"]);

export const NAME_SEARCH_MIN_LENGTH = 4;
const NAME_SEARCH_DEBOUNCE_MS = 300;
const NAME_SEARCH_TIMEOUT_ID = "add-company/name-search";

export function searchesByName(state: AddCompanyState): boolean {
  const iso2 = countryRules(state)?.iso2;
  return iso2 !== undefined && nameSearchCountries.has(iso2);
}

export function isNameQuery(
  state: AddCompanyState,
  value: string = state.identifierModel.inputValue,
): boolean {
  return searchesByName(state) && /\p{L}/u.test(value);
}

export const changeIdentifier = (state: AddCompanyState, value: string): DCEvent[] => [
  setIdentifier(value),
  ...searchByName(state, value),
];

function searchByName(state: AddCompanyState, value: string): DCEvent[] {
  const rules = countryRules(state);
  const query = value.trim();
  if (!rules || !isNameQuery(state, value) || query.length < NAME_SEARCH_MIN_LENGTH) {
    return [
      { type: "clear-timeout", timeoutId: NAME_SEARCH_TIMEOUT_ID },
      updateAddCompany({ nameSearchResult: undefined }),
    ];
  }
  return [
    debounceEvents(
      NAME_SEARCH_TIMEOUT_ID,
      NAME_SEARCH_DEBOUNCE_MS,
      [
        companyRegistryApiClient.searchCompanies(rules.iso2, query, {
          onSuccess: (res: HttpResponse<CompanySearchHit[]>) => [
            applyNameSearch(query, ok(res.data)),
          ],
          onFailure: () => [applyNameSearch(query, failure())],
        }),
      ],
      { onLeading: [updateAddCompany({ nameSearchResult: pending() })] },
    ),
  ];
}

const applyNameSearch = (query: string, result: Result<CompanySearchHit[]>): UpdateStoreEvent =>
  updateAddCompanyWith((state) =>
    state.identifierModel.inputValue.trim() === query
      ? { ...state, nameSearchResult: result }
      : state,
  );

export function nameSearchHits(state: AddCompanyState): CompanySearchHit[] {
  return isNameQuery(state) && isOk(state.nameSearchResult) ? state.nameSearchResult.data : [];
}

export function foundNoCompanies(state: AddCompanyState): boolean {
  return (
    isNameQuery(state) && isOk(state.nameSearchResult) && state.nameSearchResult.data.length === 0
  );
}

export const selectSearchHit = (hit: CompanySearchHit): DCEvent[] => [
  setIdentifier(hit.identifier),
  updateAddCompany({ nameSearchResult: undefined }),
  {
    type: "with-store",
    events: ({ addCompany }) => (addCompany ? verifyIdentifier(addCompany) : []),
  },
];

export const checkIdentifier = (): UpdateStoreEvent =>
  updateAddCompany({ identifierChecked: true });

export const checkSubdivision = (): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    addCompany: (state) => {
      if (!state) return state;
      state.subdivisionOptionModel.setError(
        hasValidSubdivision(state) ? undefined : { key: "add-company.subdivision-required" },
      );
      return { ...state };
    },
  },
});

export const verifyIdentifier = (state: AddCompanyState): DCEvent[] => {
  const rules = countryRules(state);
  if (!rules || !hasValidSubdivision(state) || !hasAcceptedIdentifier(state)) {
    return [checkSubdivision(), checkIdentifier()];
  }

  const identifier = state.identifierModel.inputValue;
  return [
    updateAddCompany({ identifierChecked: true, lookupResult: pending() }),
    companyRegistryApiClient.lookupCompany(rules.iso2, identifier, {
      onSuccess: (res: HttpResponse<CompanyLookupResponse>) => [applyLookup(res.data)],
      onFailure: () => [updateAddCompany({ lookupResult: failure() })],
    }),
  ];
};

const applyLookup = (lookup: CompanyLookupResponse): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    addCompany: (state) => {
      if (!state) return state;
      if (lookup.name) state.nameModel.setAndCommitInputValue(lookup.name);
      const reported = subdivisionOptions(countryRules(state)).find(
        (option) => option.code === lookup.subdivision,
      );
      if (reported) state.subdivisionOptionModel.setValue(reported);
      if (entityTypeFromRegister(state)) chooseEntityType(state, registerEntityType(state, lookup));
      return { ...state, lookupResult: ok(lookup) };
    },
  },
});

export function identifierRule(state: AddCompanyState): CompanyIdentifier | undefined {
  return primaryIdentifier(countryRules(state));
}

export function identifierExample(state: AddCompanyState): string | undefined {
  return identifierFor(countryRules(state), submittedSubdivision(state))?.exampleFormatted;
}

export function countryRules(state: AddCompanyState): RegistryCountry | undefined {
  return isOk(state.rulesResult) ? state.rulesResult.data : undefined;
}

export function selectedEntityType(state: AddCompanyState): EntityType | null {
  return state.entityTypeModel.value;
}

export function entityTypeFromRegister(state: AddCompanyState): boolean {
  return typedByRegister(countryRules(state));
}

function registerEntityType(
  state: AddCompanyState,
  lookup: CompanyLookupResponse,
): EntityType | null {
  return (
    countryRules(state)?.entityTypes.find((option) => option.code === lookup.entityTypeCode) ?? null
  );
}

export function asksForEntityType(state: AddCompanyState): boolean {
  if (!entityTypeFromRegister(state)) return true;
  const lookup = state.lookupResult;
  return isFailure(lookup) || (isOk(lookup) && registerEntityType(state, lookup.data) === null);
}

export function showsEntityType(state: AddCompanyState): boolean {
  return asksForEntityType(state) || selectedEntityType(state) !== null;
}

function readyForIdentifier(state: AddCompanyState): boolean {
  return !isBlocked(state) && (selectedEntityType(state) !== null || entityTypeFromRegister(state));
}

export function showsIdentifier(state: AddCompanyState): boolean {
  return countryRules(state) !== undefined && readyForIdentifier(state);
}

export function blockedMessage(state: AddCompanyState): string | null {
  const entityType = selectedEntityType(state);
  return entityType && !entityType.onboarding.allowed
    ? (entityType.onboarding.message?.[state.lang] ?? null)
    : null;
}

export function isBlocked(state: AddCompanyState): boolean {
  const entityType = selectedEntityType(state);
  return entityType !== null && !entityType.onboarding.allowed;
}

export function showsSubdivision(state: AddCompanyState): boolean {
  return readyForIdentifier(state) && requiresSubdivision(countryRules(state));
}

export function showsSubdivisionChoice(state: AddCompanyState): boolean {
  return (
    !requiresSubdivision(countryRules(state)) && subdivisionOptions(countryRules(state)).length > 0
  );
}

export function reviewNote(state: AddCompanyState): string | undefined {
  return isBlocked(state) || selectedEntityType(state) === null
    ? undefined
    : countryRules(state)?.reviewNote;
}

export function showsVerify(state: AddCompanyState): boolean {
  return readyForIdentifier(state) && (countryRules(state)?.verification.supported ?? false);
}

export function canVerify(state: AddCompanyState): boolean {
  return (
    showsVerify(state) && state.identifierModel.inputValue !== "" && !isPending(state.lookupResult)
  );
}

export function showsRemainingDetails(state: AddCompanyState): boolean {
  if (isBlocked(state) || selectedEntityType(state) === null) return false;
  if (!hasAcceptedIdentifier(state)) return false;
  if (!hasValidSubdivision(state)) return false;
  return isPastVerify(state);
}

function isPastVerify(state: AddCompanyState): boolean {
  return !showsVerify(state) || isOk(state.lookupResult) || isFailure(state.lookupResult);
}

function hasAcceptedIdentifier(state: AddCompanyState): boolean {
  const rule = identifierRule(state);
  return rule !== undefined && isAcceptedIdentifier(state.identifierModel.inputValue, rule);
}

export function hasValidSubdivision(state: AddCompanyState): boolean {
  const value = submittedSubdivision(state);
  if (value === null) return !requiresSubdivision(countryRules(state));
  return isValidSubdivision(value, countryRules(state));
}

export function subdivisionLabel(state: AddCompanyState): TMessage {
  const label = countryRules(state)?.subdivisionLabel;
  return label ? { translated: label[state.lang] } : { key: "add-company.jurisdiction" };
}

export function identifierError(state: AddCompanyState): TMessage | undefined {
  const rule = identifierRule(state);
  if (!state.identifierChecked || !rule || !state.identifierModel.inputValue) return undefined;
  if (isNameQuery(state)) return { key: "add-company.choose-search-result" };
  return hasAcceptedIdentifier(state)
    ? undefined
    : { key: "add-company.organisation-number-format", params: { example: rule.exampleFormatted } };
}

export function identifierWarning(state: AddCompanyState): TMessage | undefined {
  const rules = countryRules(state);
  const rule = identifierRule(state);
  if (!state.identifierChecked || !rules || !rule || !hasAcceptedIdentifier(state))
    return undefined;
  if (!isPastVerify(state)) return undefined;

  const value = state.identifierModel.inputValue;
  if (!isValidIdentifier(value, rule))
    return unusualNumber(rules.name[state.lang], rule.exampleFormatted);

  const issuer = chosenSubdivision(state);
  if (!issuer?.number) return undefined;
  return new RegExp(issuer.number.regex).test(normalizeIdentifier(value, rule))
    ? undefined
    : unusualNumber(issuer.label[state.lang], issuer.number.example);
}

function unusualNumber(jurisdiction: string, example: string): TMessage {
  return { key: "add-company.organisation-number-unusual", params: { jurisdiction, example } };
}

function chosenSubdivision(state: AddCompanyState): Subdivision | undefined {
  const code = submittedSubdivision(state);
  return countryRules(state)?.subdivisions.find((option) => option.code === code);
}

export function submittedSubdivision(state: AddCompanyState): string | null {
  return state.subdivisionOptionModel.value?.code ?? null;
}

export function showsSubscription(state: AddCompanyState): boolean {
  return (
    isRegisterCountry(state) || state.subscriptionModel.options.some((option) => option !== null)
  );
}

export function subscriptionModelFor(
  state: AddCompanyState,
): RadioFieldModel<AccountSubscription | null> {
  return isRegisterCountry(state) ? state.registerModel : state.subscriptionModel;
}

function subscriptionType(state: AddCompanyState): string | null {
  return subscriptionModelFor(state).value?.productName ?? null;
}

export function showsParent(state: AddCompanyState): boolean {
  return subscriptionType(state) === OWNERSHIP_REGISTER_PRODUCT;
}

export function showsParentOwnership(state: AddCompanyState): boolean {
  return showsParent(state) && state.parentModel.value !== null;
}

export function parentOptions(state: AddCompanyState): ParentOption[] {
  return state.parentModel.options.filter((option) => option !== null);
}

export function filterParents(parents: ParentOption[], query: string): ParentOption[] {
  return orderlessFilter(parents, (parent) => [parent.name, parent.orgNo], query);
}

export const selectParent = (parent: ParentOption | null): UpdateStoreEvent =>
  updateAddCompanyWith((state) => {
    state.parentModel.setValue(parent);
    return { ...state };
  });

export function isPercentHeld(state: AddCompanyState): boolean {
  return state.parentHoldings[0]?.basisModel.value === "percent-held";
}

export const selectParentBasis = (id: string, basis: ParentOwnershipBasis): UpdateStoreEvent =>
  updateAddCompanyWith((state) => {
    state.parentHoldings.find((holding) => holding.id === id)?.basisModel.setValue(basis);
    return basis === "percent-held"
      ? { ...state, parentHoldings: state.parentHoldings.slice(0, 1) }
      : { ...state };
  });

export const addParentShareClass = (): UpdateStoreEvent =>
  updateAddCompanyWith((state) => {
    const unused = shareClassCodes.find((code) => !isShareClassTaken(state, code));
    return unused
      ? {
          ...state,
          parentHoldings: [
            ...state.parentHoldings,
            newParentHolding(unused, shareClassCodes, state.lang),
          ],
        }
      : state;
  });

export const removeParentShareClass = (id: string): UpdateStoreEvent =>
  updateAddCompanyWith((state) => ({
    ...state,
    parentHoldings: state.parentHoldings.filter((holding) => holding.id !== id),
  }));

function updateAddCompanyWith(fn: (state: AddCompanyState) => AddCompanyState): UpdateStoreEvent {
  return { type: "update-store", data: { addCompany: (state) => state && fn(state) } };
}

export function isShareClassTaken(
  state: AddCompanyState,
  code: ParentOwnershipBasis,
  exceptId?: string,
): boolean {
  return state.parentHoldings.some(
    (holding) => holding.id !== exceptId && holding.basisModel.value === code,
  );
}

export function canAddParentShareClass(state: AddCompanyState): boolean {
  return !isPercentHeld(state) && state.parentHoldings.length < shareClassCodes.length;
}

export function heldError(holding: ParentHolding): TMessage | undefined {
  const held = holding.heldModel.toNumber();
  const total = holding.totalModel.toNumber();
  return held !== undefined && total !== undefined && held > total
    ? { key: "add-company.held-exceeds-total" }
    : undefined;
}

export function parentShareCount(state: AddCompanyState): number | undefined {
  const percent = state.parentOwnershipModel.toNumber();
  if (!showsParentOwnership(state) || !isPercentHeld(state) || percent === undefined) {
    return undefined;
  }
  if (percent <= 0 || percent >= FULL_OWNERSHIP) return undefined;
  return shareCountFor(percent);
}

function shareCountFor(percent: number): number {
  const fraction = percent.toFixed(OWNERSHIP_DECIMALS).split(".")[1] ?? "";
  return 10 ** (fraction.replace(/0+$/, "").length + 2);
}

function hasValidParentOwnership(state: AddCompanyState): boolean {
  if (!showsParentOwnership(state)) return true;
  return (
    parentOwnership(state) !== undefined &&
    state.parentRegistrationDateModel.toISOString() !== undefined &&
    state.parentRegistrationTimeModel.toISOString() !== undefined
  );
}

function parentOwnership(state: AddCompanyState): ParentOwnershipTerms | undefined {
  return isPercentHeld(state) ? percentHeld(state) : sharesHeld(state);
}

function percentHeld(state: AddCompanyState): ParentOwnershipTerms | undefined {
  const percent = state.parentOwnershipModel.toNumber();
  if (!state.parentOwnershipModel.isValid || percent === undefined) return undefined;
  if (percent <= 0 || percent > FULL_OWNERSHIP) return undefined;
  return { type: "percent", percentHeld: percent.toFixed(OWNERSHIP_DECIMALS) };
}

function sharesHeld(state: AddCompanyState): ParentOwnershipTerms | undefined {
  const holdings = state.parentHoldings.flatMap((holding) => {
    const code = holding.basisModel.value;
    const held = holding.heldModel.toNumber();
    const total = holding.totalModel.toNumber();
    const valid =
      code !== "percent-held" &&
      holding.heldModel.isValid &&
      holding.totalModel.isValid &&
      held !== undefined &&
      total !== undefined &&
      heldError(holding) === undefined;
    return valid ? [{ shareTypeCode: code, held, total }] : [];
  });
  const complete = holdings.length === state.parentHoldings.length;
  return complete && holdings.some((holding) => holding.held > 0)
    ? { type: "shares", holdings }
    : undefined;
}

function parentRequest(state: AddCompanyState): ParentOwnershipRequest | null {
  const parent = state.parentModel.value;
  const ownership = parentOwnership(state);
  const date = state.parentRegistrationDateModel.toISOString();
  const time = state.parentRegistrationTimeModel.toISOString();
  if (!showsParentOwnership(state) || !parent || !ownership || !date || !time) return null;
  return { orgNo: parent.orgNo, ownership, registeredAt: combineDateAndTime(date, time) };
}

export function canSubmit(state: AddCompanyState): boolean {
  if (isBlocked(state)) return false;
  const rules = countryRules(state);
  const entityType = selectedEntityType(state);
  if (!rules || !entityType) return false;
  if (!state.nameModel.inputValue.trim()) return false;
  if (!state.acceptTerms.value) return false;
  if (!hasValidSubdivision(state)) return false;
  if (!hasValidParentOwnership(state)) return false;
  return hasAcceptedIdentifier(state);
}

export const submitAddCompany = (state: AddCompanyState): DCEvent[] => {
  const rules = countryRules(state);
  const entityType = selectedEntityType(state);
  if (!rules || !entityType) return [];

  return [
    updateAddCompany({ submitResult: pending() }),
    enterpriseAccountApiClient.addCompany(
      state.accountId,
      {
        subscriptionType: subscriptionType(state),
        completeOnboarding: false,
        adminUserIds: [...(state.adminsModel?.selectedIds ?? [])],
        portfolioId: state.portfolioModel.value?.id ?? null,
        parent: parentRequest(state),
        newCompany: {
          country: rules.iso2,
          entityTypeCode: entityType.code,
          identifier: state.identifierModel.inputValue,
          subdivision: submittedSubdivision(state),
          name: state.nameModel.inputValue.trim(),
          tinNumber: state.tinNumberModel.inputValue.trim() || null,
        },
      },
      {
        onSuccess: () => [
          { type: "update-store", data: { addCompany: () => undefined } },
          { type: "reload" },
        ],
        onFailure: (err: unknown) => [
          updateAddCompany({ submitResult: failure(rejectionMessage(err)) }),
        ],
      },
    ),
  ];
};

function rejectionMessage(err: unknown): TMessage {
  const rejection = (err as { response?: { data?: NewCompanyRejection } })?.response?.data;
  switch (rejection?.code) {
    case "INVALID_IDENTIFIER":
      return {
        key: "add-company.organisation-number-format",
        params: { example: rejection.expectedExample ?? "" },
      };
    case "ALREADY_EXISTS":
      return { key: "add-company.already-exists" };
    case "IN_ANOTHER_ACCOUNT":
      return { key: "add-company.in-another-account" };
    case "ENTITY_TYPE_BLOCKED":
      return { key: "add-company.entity-type-blocked" };
    case "SUBDIVISION_REQUIRED":
      return { key: "add-company.subdivision-required" };
    case "INVALID_SUBDIVISION":
      return { key: "add-company.invalid-subdivision" };
    case "NAME_REQUIRED":
      return { key: "required" };
    case "PARENT_NOT_IN_ACCOUNT":
      return { key: "add-company.parent-not-in-account" };
    case "INVALID_PARENT_OWNERSHIP":
      return { key: "add-company.parent-ownership-invalid" };
    default:
      return { key: "add-company.failed" };
  }
}
